import { Close, FlashOff } from '@mui/icons-material';
import {
  Box,
  Grid,
  Stack,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createWorker } from 'tesseract.js';
import GenButton from './GenButton';

const Scanner = ({ closeModal }) => {
  const { t } = useTranslation('global');
  const theme = useTheme();
  const isBellowMedium = useMediaQuery(theme.breakpoints.down('md'));
  const [scannedText, setScannedText] = useState(null);
  const [scannedVin, setScannedVin] = useState(null);
  const [videoHeight, setVideoHeight] = useState(null);
  const [torch, setTorch] = useState(false);
  const myVideo = useRef();
  const myStream = useRef();

  const vidWidth = window.innerWidth;
  const vidHeight = videoHeight;
  const vidOffsetTop = window.innerHeight / 2 - vidHeight / 2;
  const vidOffsetLeft = window.innerWidth / 2 - vidWidth / 2;

  const marginX = 40;
  const indWidth = vidWidth;
  const indHeight = 80;

  const scannedCodes = useRef();

  const tick = useCallback(async () => {
    const worker = await createWorker();
    worker.reinitialize('eng', '0', { load_system_dawg: false });
    if (myVideo && myVideo.current) {
      const canvas = document.createElement('canvas');
      canvas.width = indWidth;
      canvas.height = indHeight;

      const image = myVideo.current;

      const sx = marginX / 2 / 2;
      const sy = 260 - indHeight;
      const sWidth = indWidth * 2;
      const sHeight = indHeight * 2;

      const dx = 0;
      const dy = 0;
      const dWidth = indWidth;
      const dHeight = indHeight;

      canvas
        .getContext('2d')
        .drawImage(image, sx, sy, sWidth, sHeight, dx, dy, dWidth, dHeight);

      const {
        data: { text },
      } = await worker.recognize(canvas, { config: 'psm 7' });
      const regex = /[A-Z0-9]/gi;
      const scanText =
        text &&
        text.match(regex) &&
        text
          .match(regex)
          .filter(x => x)
          .join('');

      if (scanText?.length >= 5) {
        setScannedText(scanText);
      }
      if (scanText?.length >= 15 && scanText?.length <= 20) {
        setScannedVin(scanText.toUpperCase());
      }
      (async () => {
        await worker.terminate();
      })();
      requestAnimationFrame(tick);
    }
  }, [indWidth]);

  useEffect(() => {
    if (myVideo && myVideo.current) {
      navigator.mediaDevices
        .getUserMedia({
          video: { facingMode: isBellowMedium ? 'environment' : 'user' },
          audio: false,
        })
        ?.then(stream => {
          const track = stream.getVideoTracks()[0];
          const videoSettings = track.getSettings();
          setVideoHeight(videoSettings.height);
          const video = document.querySelector('video');
          video.srcObject = stream;

          video.addEventListener('loadedmetadata', e => {
            window.setTimeout(
              () => onCapabilitiesReady(track.getCapabilities()),
              500
            );
          });

          function onCapabilitiesReady(capabilities) {
            if (capabilities.torch) {
              track.applyConstraints({
                advanced: [{ torch: torch }],
              });
            }
          }

          myVideo.current.srcObject = stream;
          myVideo.current.play();

          myStream.current = stream;
          scannedCodes.current = {};

          requestAnimationFrame(tick);
        })
        .catch(err => {
          // handle error here with popup
        });
    }

    return () =>
      myStream &&
      myStream.current &&
      myStream.current.getTracks().forEach(x => x.stop());
  }, [isBellowMedium, tick, torch]);

  return (
    <Grid
      container
      sx={{
        backgroundColor: theme.palette.common.black,
        width: '100dvw',
        height: '100dvh',
        flexDirection: 'column',
        position: 'relative',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          top: '0px',
          left: '0px',
          zIndex: 4,
          backgroundColor: alpha(theme.palette.common.black, 0.2),
        }}
      >
        <Stack direction="column" sx={{ width: '100%', height: '100%' }}>
          <Box
            sx={{
              width: '100%',
              height: `calc(50% - ${indHeight / 2}px)`, // '50%',
              backgroundColor: alpha(theme.palette.common.black, 0.8),
            }}
          >
            <Stack
              direction="row"
              alignItems="flex-start"
              justifyContent="space-between"
              sx={{ width: '100%', height: '100%', p: 1 }}
            >
              <GenButton
                size="large"
                variant="outlined"
                sx={{
                  color: theme.palette.common.white,
                }}
                iconForIconButton={<Close />}
                onClick={() => closeModal(scannedVin)}
              />
              <GenButton
                size="large"
                variant="outlined"
                sx={{
                  color: theme.palette.common.white,
                }}
                iconForIconButton={<FlashOff />}
                onClick={() => setTorch(state => !state)}
              />
            </Stack>
          </Box>
          <Box
            sx={{
              width: '100%',
              height: indHeight,
            }}
          ></Box>
          <Box
            sx={{
              width: '100%',
              height: `calc(50% - ${indHeight / 2}px)`,
              backgroundColor: alpha(theme.palette.common.black, 0.8),
            }}
          >
            <Stack
              direction="column"
              alignItems="center"
              justifyContent="center"
              sx={{ width: '100%', height: '100%' }}
            >
              <Typography
                variant="caption"
                sx={{ color: theme.palette.common.white }}
              >
                {`${t('scanner.currentScan')}${
                  scannedText ? scannedText : t('scanner.scanning')
                }`}
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: theme.palette.common.white }}
              >
                {`${t('scanner.scannedVin')}${
                  scannedVin ? scannedVin : t('scanner.scanning')
                }`}
              </Typography>
            </Stack>
          </Box>
        </Stack>
      </Box>
      <video
        ref={myVideo}
        autoPlay
        muted
        playsInline
        width={vidWidth}
        height={vidHeight}
        style={{
          position: 'absolute',
          top: vidOffsetTop,
          left: vidOffsetLeft,
          zIndex: 2,
        }}
      ></video>
    </Grid>
  );
};

export default Scanner;
